
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SearchButton } from "@/components/SearchDialog";
import { useTheme } from "next-themes";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import SubscriptionForm from "@/components/SubscriptionForm";
import { useState, useEffect } from "react";

const NavBar = () => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const { theme } = useTheme();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const NavLinks = () => (
    <>
      <Link
        to="/"
        className={cn(
          "transition-colors hover:text-blog-neon relative",
          isActive("/") 
            ? "text-blog-neon after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:w-full after:h-[2px] after:bg-blog-neon after:rounded-full" 
            : "text-foreground/70"
        )}
      >
        Home
      </Link>
      <Link
        to="/archive"
        className={cn(
          "transition-colors hover:text-blog-neon relative",
          isActive("/archive") 
            ? "text-blog-neon after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:w-full after:h-[2px] after:bg-blog-neon after:rounded-full" 
            : "text-foreground/70"
        )}
      >
        Archive
      </Link>
      <Link
        to="/about"
        className={cn(
          "transition-colors hover:text-blog-neon relative",
          isActive("/about") 
            ? "text-blog-neon after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:w-full after:h-[2px] after:bg-blog-neon after:rounded-full" 
            : "text-foreground/70"
        )}
      >
        About
      </Link>
      {user && (
        <Link
          to="/admin/dashboard"
          className={cn(
            "transition-colors hover:text-blog-neon relative",
            isActive("/admin/dashboard") 
              ? "text-blog-neon after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:w-full after:h-[2px] after:bg-blog-neon after:rounded-full" 
              : "text-foreground/70"
          )}
        >
          Dashboard
        </Link>
      )}
    </>
  );

  return (
    <header className={cn(
      "sticky top-0 z-10 w-full border-b",
      theme === "light" 
        ? "bg-background border-gray-200" 
        : "border-futuristic-border/30 backdrop-blur-xl bg-futuristic-bg-dark/60 supports-[backdrop-filter]:bg-futuristic-bg-dark/30"
    )}>
      <div className="container flex h-16 max-w-screen-xl items-center">
        <div className="mr-4 flex items-center justify-between w-full">
          <div className="flex items-center">
            <Link to="/" className="mr-6 flex items-center space-x-2">
              <span className="text-xl font-bold text-gradient futuristic-title">AI Genesis</span>
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6 text-sm font-medium">
              <NavLinks />
            </nav>
          </div>
          
          <div className="flex items-center space-x-2 md:space-x-4">
            {!isMobile && (
              <Sheet>
                <SheetTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-futuristic-border text-blog-neon hover:bg-blog-neon/10"
                  >
                    Subscribe
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-full sm:max-w-[400px]">
                  <div className="px-1 py-6">
                    <h2 className="text-2xl font-bold mb-2">Subscribe to AI Genesis</h2>
                    <p className="text-muted-foreground mb-6">
                      Get notified when new articles are published. No spam, unsubscribe anytime.
                    </p>
                    <SubscriptionForm className="max-w-md" />
                  </div>
                </SheetContent>
              </Sheet>
            )}
            
            <SearchButton />
            <ThemeToggle />
            
            {user ? (
              <div className="hidden md:flex items-center gap-4">
                <span className="text-sm text-muted-foreground truncate max-w-[150px]">
                  {user.email}
                </span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => signOut()}
                  className="border-futuristic-border text-blog-neon hover:bg-blog-neon/10"
                >
                  Sign Out
                </Button>
              </div>
            ) : (
              <Link to="/admin/login" className="hidden md:block">
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="hover:bg-blog-neon/10 hover:text-blog-neon"
                >
                  Admin Login
                </Button>
              </Link>
            )}
            
            {/* Mobile menu */}
            <Sheet>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[80%] max-w-xs">
                <div className="flex flex-col space-y-6 py-6">
                  <nav className="flex flex-col space-y-4 text-base font-medium">
                    <NavLinks />
                  </nav>
                  
                  <div className="space-y-4 pt-4 border-t">
                    <Sheet>
                      <SheetTrigger asChild className="w-full">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="border-futuristic-border text-blog-neon hover:bg-blog-neon/10 w-full"
                        >
                          Subscribe
                        </Button>
                      </SheetTrigger>
                      <SheetContent className="w-full">
                        <div className="px-1 py-6">
                          <h2 className="text-2xl font-bold mb-2">Subscribe to AI Genesis</h2>
                          <p className="text-muted-foreground mb-6">
                            Get notified when new articles are published. No spam, unsubscribe anytime.
                          </p>
                          <SubscriptionForm className="max-w-md" />
                        </div>
                      </SheetContent>
                    </Sheet>
                    
                    {user ? (
                      <div className="space-y-2">
                        <div className="text-sm text-muted-foreground truncate">
                          {user.email}
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => signOut()}
                          className="border-futuristic-border text-blog-neon hover:bg-blog-neon/10 w-full"
                        >
                          Sign Out
                        </Button>
                      </div>
                    ) : (
                      <Link to="/admin/login" className="block w-full">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="hover:bg-blog-neon/10 hover:text-blog-neon w-full"
                        >
                          Admin Login
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default NavBar;
